import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabaseClient';
import { DocumentTextIcon } from '@heroicons/react/24/outline';

export function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/contracts');
    }
  }, [user, navigate]);

  const handleSignIn = async () => {
    const toastId = toast.loading('Redirecting to Microsoft login...');
    
    try {
      // Sign in with Azure AD (Entra ID) via Supabase
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email openid profile',
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Failed to sign in:', error);
      toast.error('Failed to sign in with Azure AD. Please try again.', { id: toastId });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-gradient-to-br from-primary-600 to-primary-800 p-12 flex-col justify-between relative overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <DocumentTextIcon className="w-9 h-9 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Klarinet CLM</h1>
              <p className="text-sm text-white/80">Contract Lifecycle Management</p>
            </div>
          </div>

          {/* Simple tagline */}
          <div className="space-y-4">
            <h2 className="text-5xl font-bold text-white leading-tight">
              Contract<br />Management<br />Platform
            </h2>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-white/60 text-sm">
          © 2025 Klarinet Solutions, LLC. All rights reserved.
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
              <h1 className="text-2xl font-bold text-gray-900">Klarinet CLM</h1>
                <p className="text-sm text-gray-600">Contract Management</p>
              </div>
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign In</h2>
              <p className="text-gray-600">
                Use your organization account
              </p>
            </div>

            {/* Sign In Button */}
            <button
              onClick={handleSignIn}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              {/* Microsoft Logo */}
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                <path d="M0 0h11v11H0z" />
                <path d="M12 0h11v11H12z" />
                <path d="M0 12h11v11H0z" />
                <path d="M12 12h11v11H12z" />
              </svg>
              Sign in with Microsoft
            </button>

            <p className="text-center text-xs text-gray-500 mt-4">
              Secured with Microsoft Entra ID
            </p>
          </div>

          {/* Help Text */}
          <p className="text-center text-sm text-gray-600 mt-6">
            Need help? Contact your system administrator
          </p>
        </div>
      </div>
    </div>
  );
}

